import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Plus, Save, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialNodes = [];
const initialEdges = [];

const WorkflowBuilder = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [workflows, setWorkflows] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDesc, setWorkflowDesc] = useState('');

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API}/workflows`);
      setWorkflows(response.data);
    } catch (error) {
      console.error('Error fetching workflows:', error);
    }
  };

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const addNode = (type) => {
    const newNode = {
      id: `${type}-${Date.now()}`,
      type: 'default',
      position: { x: Math.random() * 400, y: Math.random() * 400 },
      data: { label: `${type} Node` },
      style: {
        background: type === 'agent' ? '#8b5cf6' : type === 'condition' ? '#06b6d4' : '#f59e0b',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '10px',
      },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const saveWorkflow = async () => {
    try {
      await axios.post(`${API}/workflows`, {
        name: workflowName,
        description: workflowDesc,
        nodes: nodes,
        edges: edges,
      });
      toast.success('Workflow saved successfully');
      setIsDialogOpen(false);
      fetchWorkflows();
    } catch (error) {
      console.error('Error saving workflow:', error);
      toast.error('Failed to save workflow');
    }
  };

  const loadWorkflow = async (workflowId) => {
    try {
      const response = await axios.get(`${API}/workflows/${workflowId}`);
      setNodes(response.data.nodes || []);
      setEdges(response.data.edges || []);
      toast.success('Workflow loaded');
    } catch (error) {
      console.error('Error loading workflow:', error);
      toast.error('Failed to load workflow');
    }
  };

  return (
    <div className="animate-fade-in" data-testid="workflow-builder-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Workflow Builder
          </h1>
          <p className="text-sm sm:text-base text-gray-400">Design and orchestrate multi-agent workflows</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <Button onClick={() => addNode('agent')} className="btn-secondary" data-testid="add-agent-node-btn">
            <Plus className="w-4 h-4 mr-2" />
            Agent Node
          </Button>
          <Button onClick={() => addNode('condition')} className="btn-secondary" data-testid="add-condition-node-btn">
            <Plus className="w-4 h-4 mr-2" />
            Condition
          </Button>
          <Button onClick={() => addNode('action')} className="btn-secondary" data-testid="add-action-node-btn">
            <Plus className="w-4 h-4 mr-2" />
            Action
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="save-workflow-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Workflow
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-900 border border-gray-700 text-white">
              <DialogHeader>
                <DialogTitle>Save Workflow</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Workflow Name</label>
                  <Input
                    placeholder="Enter workflow name"
                    value={workflowName}
                    onChange={(e) => setWorkflowName(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                    data-testid="workflow-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Description</label>
                  <Input
                    placeholder="Enter description"
                    value={workflowDesc}
                    onChange={(e) => setWorkflowDesc(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                    data-testid="workflow-desc-input"
                  />
                </div>
                <Button onClick={saveWorkflow} className="w-full btn-primary" data-testid="submit-workflow-btn">
                  Save
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Workflow Canvas */}
        <div className="lg:col-span-3 glass-panel p-0 overflow-hidden" style={{ height: '500px' }} data-testid="workflow-canvas">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            style={{ background: 'rgba(10, 14, 39, 0.5)' }}
          >
            <Controls />
            <MiniMap style={{ background: 'rgba(0, 0, 0, 0.8)' }} />
            <Background color="#4f46e5" gap={16} />
          </ReactFlow>
        </div>

        {/* Saved Workflows */}
        <div className="glass-panel p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Saved Workflows</h3>
          <div className="space-y-3" data-testid="saved-workflows-list">
            {workflows.map((workflow) => (
              <div
                key={workflow.id}
                className="p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
                onClick={() => loadWorkflow(workflow.id)}
                data-testid={`workflow-item-${workflow.id}`}
              >
                <p className="text-white font-medium text-sm">{workflow.name}</p>
                <p className="text-gray-400 text-xs mt-1">{workflow.status}</p>
              </div>
            ))}
            {workflows.length === 0 && (
              <p className="text-gray-400 text-sm text-center py-8">No workflows yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowBuilder;